'use client'

import type { PricingProps } from 'mdxui'
import { PricingPlans } from './pricing-plans'
import { PricingCompact } from './pricing-compact'
import {
  mapToPricingPlansProps,
  mapToPricingCompactProps,
  PRICING_VARIANT_MAP,
  type PricingVariant,
} from './mappers'

// =============================================================================
// Pricing Adapter
// =============================================================================

export interface PricingAdapterProps extends Omit<PricingProps, 'variant'> {
  /** Layout variant - determines which block component to use */
  variant?: PricingVariant
}

/**
 * Pricing Adapter
 *
 * Renders mdxui PricingProps using shadcnblocks pricing components.
 * Select variant to control the visual style:
 * - simple: Single-tier focused display (PricingCompact)
 * - comparison: Multi-tier comparison layout (PricingPlans)
 * - toggle: Monthly/yearly toggle display (PricingPlans)
 *
 * @example
 * ```tsx
 * <Pricing
 *   title="Simple Pricing"
 *   tiers={[
 *     { name: 'Pro', price: '$29/mo', features: ['...'], callToAction: 'Start' }
 *   ]}
 *   variant="comparison"
 * />
 * ```
 */
export function Pricing({ variant = 'comparison', ...props }: PricingAdapterProps) {
  const blockName = PRICING_VARIANT_MAP[variant]

  switch (blockName) {
    case 'PricingPlans':
      return <PricingPlans {...mapToPricingPlansProps(props)} />
    case 'PricingCompact':
      return <PricingCompact {...mapToPricingCompactProps(props)} />
    default:
      return <PricingPlans {...mapToPricingPlansProps(props)} />
  }
}

// =============================================================================
// Semantic Variant Components
// =============================================================================

/**
 * PricingSimple - Single-tier focused display
 *
 * Best for: Single pricing tier, simple offerings, free tools
 * Uses: PricingCompact block
 */
export function PricingSimple(props: Omit<PricingProps, 'variant'>) {
  return <PricingCompact {...mapToPricingCompactProps(props)} />
}

/**
 * PricingToggle - Monthly/yearly toggle display
 *
 * Best for: Subscription pricing with annual discounts
 * Uses: PricingPlans block
 */
export function PricingToggle(props: Omit<PricingProps, 'variant'>) {
  return <PricingPlans {...mapToPricingPlansProps(props)} />
}
